import { ChangeDetectionStrategy, Component, effect, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import Swal from 'sweetalert2';
import { ServicioVersion } from '@app/services/version/servicio-version';
import { ServicioSesion } from '@app/services/sesion/sesion.service';
import { NotaChangelog } from '@app/models/version/version-app.model';

@Component({
  selector: 'app-banner-nueva-version',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: '',
})
export class BannerNuevaVersionComponent {
  private _servicioVersion = inject(ServicioVersion);
  private _sesion = inject(ServicioSesion);
  private _usuarioActual = toSignal(this._sesion.usuarioActual$);

  private _yaMostroAvisoRecarga = false;
  private _yaMostroNovedades = false;

  constructor() {
    effect(() => {
      if (this._servicioVersion.hayNuevaVersion() && !this._yaMostroAvisoRecarga) {
        this._yaMostroAvisoRecarga = true;
        this.mostrarAvisoRecarga();
      }
    });

    effect(() => {
      if (this._servicioVersion.hayNovedadesPendientes() && !this._yaMostroNovedades && this._usuarioActual()) {
        this._yaMostroNovedades = true;
        this.mostrarNovedades();
      }
    });
  }

  private mostrarAvisoRecarga(): void {
    Swal.fire({
      icon: 'info',
      title: 'Hay una nueva versión disponible',
      text: 'Recarga la página para ver los últimos cambios.',
      showCancelButton: true,
      confirmButtonText: 'Recargar ahora',
      cancelButtonText: 'Más tarde',
    }).then((resultado) => {
      if (resultado.isConfirmed) {
        window.location.reload();
      } else {
        this._yaMostroAvisoRecarga = false;
      }
    });
  }

  private mostrarNovedades(): void {
    const ambitosUsuario = new Set(this._sesion.getContextos().map((contexto) => contexto.tipo));
    const notas = this._servicioVersion.obtenerNotasParaAmbitos(ambitosUsuario);

    if (notas.length === 0) {
      this._servicioVersion.marcarNovedadesComoVistas();
      return;
    }

    Swal.fire({
      icon: 'success',
      title: `Novedades de la versión ${this._servicioVersion.obtenerVersionActual()}`,
      html: this.armarHtmlNovedades(notas),
      confirmButtonText: 'Entendido',
      width: 520,
    }).then(() => {
      this._servicioVersion.marcarNovedadesComoVistas();
    });
  }

  private armarHtmlNovedades(notas: NotaChangelog[]): string {
    const deGlobal = notas.filter((nota) => nota.ambitos.includes('global'));
    const generales = notas.filter((nota) => nota.ambitos.length === 0);
    const deEscuela = notas.filter((nota) => nota.ambitos.includes('escuela'));

    const lista = (items: NotaChangelog[]) =>
      `<ul style="margin:0 0 12px;padding-left:20px;font-size:14px;line-height:1.5">${items
        .map((nota) => `<li>${nota.texto}</li>`)
        .join('')}</ul>`;

    const encabezado = (texto: string, color = '#6c63ff') =>
      `<div style="font-size:11px;font-weight:700;letter-spacing:.04em;color:${color};text-transform:uppercase;margin:14px 0 6px">${texto}</div>`;

    let html = '<div style="text-align:left;max-height:340px;overflow-y:auto;padding-right:4px">';
    if (deGlobal.length) html += encabezado('Info ROOT', '#e53935') + lista(deGlobal);
    if (generales.length) html += lista(generales);
    if (deEscuela.length) html += encabezado('Mejoras para tu escuela') + lista(deEscuela);
    html += '</div>';

    return html;
  }
}
